import React, { JSX, FC, useEffect, useState, Dispatch, SetStateAction } from "react";
import axios from "axios";
import * as CategoriesAPI from "api/categories";
import { useSelector } from "react-redux";
import { AppStateType } from "redux/reducers/mainReducer";
import {
  buildCategoryTreeFromChain,
  collectExpandedKeys,
  collectTreeKeys,
  mergeDuplicateNodes,
  mergeTrees,
  OptionType,
  transformDataToCategory,
  updateCategory
} from "utils/formatCategories";
import { CategoryTreeRequestType, CategoryTreeType } from "categories/types";
import { handleError } from "utils/handleError";
import useCancelableDebounce from "hooks/useCancelableDebounce";
import { Form, TreeSelect } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { IFormFields } from "pages/ImportMasterPage";
import { SafeKey } from "antd/es/table/interface";

interface ICategoriesProps {
  form: FormInstance<IFormFields>;
  handleFocus: () => void;
  updateCharc: (e: React.FocusEvent<HTMLElement, Element>) => void;
  selectedCategoryOption: OptionType[];
  setSelectedCategoryOption: Dispatch<SetStateAction<OptionType[]>>;
  fetchCategory: (params: CategoryTreeRequestType) => Promise<CategoryTreeType>;
  isFieldsDisabled: boolean;
  buildCategoryOptions: (categoryData: CategoryTreeType, includeChildrenIds: boolean) => OptionType[];
}

const Categories: FC<ICategoriesProps> = ({
  form,
  handleFocus,
  updateCharc,
  selectedCategoryOption,
  setSelectedCategoryOption,
  fetchCategory,
  isFieldsDisabled,
  buildCategoryOptions,
}): JSX.Element => {
  const { list: categoriesTreeList } = useSelector((state: AppStateType) => state.categoriesSlice?.categoriesTree);

  const [categoryOptionsDump, setCategoryOptionsDump] = useState<OptionType[]>(
    transformDataToCategory(categoriesTreeList || [])
  );
  const [categoryOptions, setCategoryOptions] = useState<OptionType[]>(
    transformDataToCategory(categoriesTreeList || [])
  );
  const [isBlur, setIsBlur] = useState<boolean>(true);
  const [expandedKeys, setExpandedKeys] = useState<SafeKey[]>([]);

  useEffect(() => {
    // если выбрали категорию с использованием поиска, то объединяем выбранную ветку с предыдущей версией дерева
    if (isBlur && selectedCategoryOption?.length) {
      buildEnrichedTreeArray(selectedCategoryOption[0])
        .then((categoriesTree: OptionType[]) => {
          const mergedCategoriesTrees: OptionType[] = mergeTrees(
            categoryOptionsDump,
            buildCategoryTreeFromChain(categoriesTree)
          );

          setCategoryOptionsDump(mergedCategoriesTrees);
          setCategoryOptions(mergedCategoriesTrees);
          setExpandedKeys(collectTreeKeys(selectedCategoryOption, true));
          setIsBlur(false);
        });
    }

    if (!selectedCategoryOption?.length) {
      setExpandedKeys([]);
    }
  }, [isBlur, selectedCategoryOption?.length]);

  useEffect(() => {
    if (!categoryOptionsDump?.length) {
      setCategoryOptionsDump(transformDataToCategory(categoriesTreeList ?? []));
    }

    if (!categoryOptions?.length) {
      setCategoryOptions(transformDataToCategory(categoriesTreeList ?? []));
    }
  }, [categoriesTreeList]);

  // Подстановка выбранной категории
  useEffect(() => {
    if (selectedCategoryOption) {
      setCategoryOptions((prevOptions) => {
        return [
          ...prevOptions.filter((opt) => opt.id !== selectedCategoryOption?.at(0)?.id),
          ...selectedCategoryOption
        ];
      });
    }
  }, [selectedCategoryOption]);

  const buildEnrichedTreeArray = async (initialNode: OptionType): Promise<OptionType[]> => {
    const enrichedArray: OptionType[] = [];

    const traversePrimaryTree = async (node: OptionType): Promise<void> => {
      if (node.children && node.children.length > 1) {
        enrichedArray.push({ ...node });

        return;
      }

      const categoryData: CategoryTreeType = await fetchCategory({ category_ids: [node.id] });

      const enrichedNode: OptionType = {
        ...node,
        children: transformDataToCategory(categoryData?.children_ids)
      };

      enrichedArray.push(enrichedNode);

      if (node.children && node.children.length === 1) {
        await traversePrimaryTree(node.children[0]);
      }
    };

    await traversePrimaryTree(initialNode);

    return enrichedArray;
  };

  const { debouncedFunction: searchCategories } = useCancelableDebounce((params, token) => {
    CategoriesAPI.getCategoriesTPAllTree(params, token)
      .then(({ data }) => {
        const newCategories: OptionType[] = mergeDuplicateNodes(
          data?.flatMap((category) => buildCategoryOptions(category, false))
        );

        setExpandedKeys(collectTreeKeys(newCategories, false));
        setCategoryOptions(newCategories);
      })
      .catch((error: any) => {
        if (!axios.isCancel(error)) {
          handleError("Ошибка при загрузке категорий.", error);
        }
      });
  }, 600);

  const updateOrAddOption = (prevOptions: OptionType[], id: number, children: OptionType[]): OptionType[] => {
    if (!updateCategory(prevOptions, id, children)) {
      return [...prevOptions, children] as OptionType[];
    }

    return updateCategory([...prevOptions], id, children);
  };

  const loadCategoryHandler = async (selectedOption: OptionType): Promise<void> => {
    const { value, children } = selectedOption ?? {};

    if (children) return;

    selectedOption.loading = true;

    try {
      const category: CategoryTreeType = await fetchCategory({ category_ids: [Number(value)] });

      const transformedChildren: OptionType[] = transformDataToCategory([category]);

      selectedOption.loading = false;

      setCategoryOptions((prevOptions) => updateOrAddOption(prevOptions, value, transformedChildren));
      setCategoryOptionsDump((prevOptions) => updateOrAddOption(prevOptions, value, transformedChildren));
    } catch (error) {
      selectedOption.loading = false;

      handleError("Ошибка при загрузке категорий", error);
    }
  };

  const searchCategory = (value: string): void => {
    if (value?.length >= 3 ) {
      searchCategories({ search: value });
    } else if (value?.length === 0) {
      setCategoryOptions(categoryOptionsDump);
      setExpandedKeys(selectedCategoryOption?.length ? collectExpandedKeys(selectedCategoryOption) : []);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLElement, Element>): void => {
    updateCharc(e);
    setIsBlur(true);
  };

  const onFocusHandle = (): void => {
    handleFocus();
    setIsBlur(false);
  };

  const handleChange = (value: number): void => {
    // Ищем путь от корня до выбранного узла
    const findPathInTree = (nodes: OptionType[], id: number, path: OptionType[] = []): OptionType[] | null => {
      for (const node of nodes) {
        const newPath: OptionType[] = [...path, node];

        if (node.id === id) {
          return newPath;
        }

        if (node.children && node.children.length > 0) {
          const result = findPathInTree(node.children, id, newPath);

          if (result) return result;
        }
      }
      return null;
    };

    if (!value) {
      form.setFieldsValue({ categoryBK: null });
      setCategoryOptions(categoryOptionsDump);
      setExpandedKeys([]);

      return;
    }

    const path: OptionType[] = findPathInTree(categoryOptions, value);

    if (path) {
      const tree: OptionType[] = buildCategoryTreeFromChain(path);

      setSelectedCategoryOption(tree);
    }
  };

  const handleClear = (): void => {
    setSelectedCategoryOption([]);
    setExpandedKeys([]);
  };

  return (
    <Form.Item
      key="categoryBkField"
      name="categoryBK"
      label="Категория БК"
      className="mb-4"
    >
      <TreeSelect
        size="large"
        showSearch
        onSearch={searchCategory}
        filterTreeNode={false}
        placeholder="Найти категорию БК"
        onFocus={onFocusHandle}
        onBlur={handleBlur}
        onChange={handleChange}
        loadData={(option) => loadCategoryHandler(option as unknown as OptionType)}
        treeData={categoryOptions}
        disabled={isFieldsDisabled}
        notFoundContent="Категория БК не найдена"
        treeExpandedKeys={expandedKeys}
        onTreeExpand={(keys: React.Key[]) => setExpandedKeys(keys as SafeKey[])}
        virtual={false}
        allowClear
        onClear={handleClear}
      />
    </Form.Item>
  );
};

export default Categories;
